/**
 * net/session.ts — a sessão do GESTOR (usuário logado no navegador).
 *
 * Regra dura do HYBRID-ARCHITECTURE §2: o **access token vive em memória** (uma
 * variável do módulo, morre com a aba) e **só o refresh vai para
 * `localStorage`**. Nada de token em URL, log, `console`, `document.title` ou
 * mensagem de erro.
 *
 * TRÊS COISAS QUE O REFRESH ROTATIVO OBRIGA (API-CONTRACT §2.3):
 *  1. **Single-flight** — duas chamadas simultâneas compartilham UM request.
 *  2. **Trava entre abas** — o reuso é detectado e revoga a FAMÉLIA INTEIRA. Duas
 *     abas do gestor chamando `/auth/refresh` com o mesmo `rt_` deslogam as
 *     duas. `BroadcastChannel` quando existe; `localStorage` (evento `storage`,
 *     que só dispara em OUTRA aba) como fallback.
 *  3. **Persistir o par novo a cada rotação** — senão a próxima aba usa um token
 *     morto e o lojista é despejado.
 *
 * ponytail: sem classe de request/interceptor. `call()` é o refresh-no-401 do
 * `apiFetch` e é o único ponto que fala com o backend de gerência.
 */
import { apiFetch, ApiError, type ApiFetchOptions, type ApiRequest } from './apiFetch';
import { resolveApiBaseUrl } from './config';
import { browserLocalStorage, memoryStore, type KeyValueStore } from './credentials';

/** Chave do refresh token em disco — contrato do plano §1.3. */
export const MANAGER_STORAGE_KEY = 'simpleindor.manager.v1';

/** Canal/lock entre abas do gestor. */
const TAB_CHANNEL = 'simpleindor.manager.v1/refresh';
const LOCK_KEY = `${MANAGER_STORAGE_KEY}/lock`;
const LOCK_TTL_MS = 10_000;

export interface StoredSession {
  refreshToken: string;
  accountId: string;
  userId: string;
  savedAt: string;
}

export interface Account {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}

export interface ManagerUser {
  id: string;
  email: string;
  name: string;
}

export interface AuthResult {
  account: Account;
  user: ManagerUser;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** `load()` devolve `null` para TODO estado inaproveitável (ausente/corrompido). */
function decodeStored(raw: string | null): StoredSession | null {
  if (raw === null || raw === '') {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed?.refreshToken !== 'string' || parsed.refreshToken === '') {
      return null;
    }
    if (typeof parsed.accountId !== 'string' || typeof parsed.userId !== 'string') {
      return null;
    }
    return {
      refreshToken: parsed.refreshToken,
      accountId: parsed.accountId,
      userId: parsed.userId,
      savedAt: typeof parsed.savedAt === 'string' ? parsed.savedAt : new Date(0).toISOString(),
    };
  } catch {
    return null;
  }
}

export interface SessionOptions {
  store?: KeyValueStore | null;
  baseUrl?: string;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
  now?: () => Date;
  onLog?: (message: string, detail?: unknown) => void;
  /** Notifica a casca de que a sessão morreu (401 de refresh) — ela volta a /entrar. */
  onSessionLost?: () => void;
  /** Timeout do refresh de boot (ms). Rede lenta não pode deixar a tela branca. */
  bootTimeoutMs?: number;
}

export class Session {
  private readonly store: KeyValueStore;
  private readonly api: ApiFetchOptions;
  private readonly now: () => Date;
  private readonly log: (message: string, detail?: unknown) => void;
  private readonly onSessionLost: (() => void) | undefined;
  private readonly bootTimeoutMs: number;
  /** MEMÓRIA do módulo: o access token nunca vai para o disco (HYBRID §7). */
  private access: string | null = null;
  private stored: StoredSession | null = null;
  private refreshing: Promise<string | null> | null = null;
  private channel: BroadcastChannel | null = null;
  private tabId: string;

  constructor(options: SessionOptions = {}) {
    this.store = options.store ?? browserLocalStorage() ?? memoryStore();
    this.api = {
      ...(options.baseUrl !== undefined ? { baseUrl: options.baseUrl } : { baseUrl: resolveApiBaseUrl() }),
      ...(options.fetchImpl ? { fetchImpl: options.fetchImpl } : {}),
      accessToken: () => this.access,
    };
    this.now = options.now ?? (() => new Date());
    this.log = options.onLog ?? (() => {});
    this.onSessionLost = options.onSessionLost;
    this.bootTimeoutMs = options.bootTimeoutMs ?? 5_000;
    this.tabId = Math.random().toString(36).slice(2, 10);
    this.stored = this.readStored();
    this.listenTabs();
  }

  /** `true` quando há refresh em disco (ainda não significa sessão válida). */
  get hasStoredSession(): boolean {
    return this.stored !== null;
  }

  get accountId(): string | null {
    return this.stored?.accountId ?? null;
  }

  get userId(): string | null {
    return this.stored?.userId ?? null;
  }

  /* ------------------------------------------------------------------ */
  /* Trava entre abas                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * Ouve a rotação feita por outra aba.
   *
   * O PAR NOVO vem no próprio evento (via `BroadcastChannel`), porque o ACCESS
   * TOKEN vive só em memória: sem ele, a aba que esperou ficaria sem token
   * nenhum e o próximo `call()` tentaria rotacionar de novo — exatamente o reuso
   * que revoga a família. O canal é same-origin; o token já está em memória numa
   * aba irmã e NUNCA é persistido.
   */
  private listenTabs(): void {
    try {
      if (typeof BroadcastChannel === 'function') {
        this.channel = new BroadcastChannel(TAB_CHANNEL);
        this.channel.addEventListener('message', (event: MessageEvent) => {
          const data = event.data as { type?: string; tabId?: string; accessToken?: string; refreshToken?: string } | null;
          if (data?.type !== 'rotated' || data.tabId === this.tabId) {
            return;
          }
          if (typeof data.accessToken === 'string' && data.accessToken !== '') {
            this.access = data.accessToken;
            this.stored = this.readStored();
          } else {
            // Versão antiga de outra aba: relê o disco (o access é a única lacuna).
            this.stored = this.readStored();
          }
        });
        return;
      }
    } catch {
      // Sem BroadcastChannel: cai no evento `storage`, que só dispara em outra aba.
    }
    try {
      globalThis.addEventListener?.(
        'storage',
        event => {
          if ((event as StorageEvent).key === MANAGER_STORAGE_KEY) {
            this.stored = this.readStored();
          }
        },
        { passive: true },
      );
    } catch {
      // Sem `storage`: cada aba roda sozinha (degradação, não crash).
    }
  }

  /** Publica o par novo para as outras abas (o access NÃO vai para o disco). */
  private broadcastRotated(accessToken?: string): void {
    try {
      this.channel?.postMessage({ type: 'rotated', tabId: this.tabId, accessToken, refreshToken: this.stored?.refreshToken });
    } catch {
      // Canal fechado: a outra aba só percebe no próximo refresh dela.
    }
  }

  /**
   * Trava de exclusão para o refresh. Devolve `true` quando a aba PODE rotacionar.
   * `localStorage` não tem CAS: a trava é o último `savedAt` vencer, e quem
   * ganhou reescreve com o próprio id. Falha de storage = pode rotacionar
   * (numa única aba o single-flight já basta).
   */
  private acquireRefreshLock(): boolean {
    try {
      const raw = this.store.getItem(LOCK_KEY);
      const existing = raw ? (JSON.parse(raw) as { tabId?: string; at?: number }) : null;
      const age = existing?.at ? this.now().getTime() - existing.at : Infinity;
      if (existing && existing.tabId !== this.tabId && age < LOCK_TTL_MS) {
        return false;
      }
      this.store.setItem(LOCK_KEY, JSON.stringify({ tabId: this.tabId, at: this.now().getTime() }));
      return true;
    } catch {
      return true;
    }
  }

  private releaseRefreshLock(): void {
    try {
      const raw = this.store.getItem(LOCK_KEY);
      const existing = raw ? (JSON.parse(raw) as { tabId?: string } | null) : null;
      if (!existing || existing.tabId === this.tabId) {
        this.store.removeItem(LOCK_KEY);
      }
    } catch {
      // Storage indisponível: a trava expira sozinha em LOCK_TTL_MS.
    }
  }

  /* ------------------------------------------------------------------ */
  /* Armazenamento                                                       */
  /* ------------------------------------------------------------------ */

  private readStored(): StoredSession | null {
    try {
      return decodeStored(this.store.getItem(MANAGER_STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private save(result: AuthResult): void {
    this.access = result.accessToken;
    const record: StoredSession = {
      refreshToken: result.refreshToken,
      accountId: result.account.id,
      userId: result.user.id,
      savedAt: this.now().toISOString(),
    };
    this.stored = record;
    try {
      this.store.setItem(MANAGER_STORAGE_KEY, JSON.stringify(record));
      this.broadcastRotated(result.accessToken);
    } catch {
      // Storage negando escrita: a sessão vive só nesta aba (modo anônimo).
      // Não é motivo para crash — o `onLog` é diagnóstico, nunca tela de erro.
      this.log('sessão não persistida — storage indisponível');
    }
  }

  private clear(): void {
    this.access = null;
    this.stored = null;
    try {
      this.store.removeItem(MANAGER_STORAGE_KEY);
      this.store.removeItem(LOCK_KEY);
      this.broadcastRotated();
    } catch {
      // Já sem storage: o estado desejado (sem sessão) é o atual.
    }
  }

  /* ------------------------------------------------------------------ */
  /* Operações                                                           */
  /* ------------------------------------------------------------------ */

  async login(email: string, password: string): Promise<AuthResult> {
    const result = await apiFetch<AuthResult>(this.api, {
      method: 'POST',
      path: '/auth/login',
      body: { email, password },
      anonymous: true,
    });
    this.save(result);
    return result;
  }

  async signup(name: string, email: string, password: string): Promise<AuthResult> {
    const result = await apiFetch<AuthResult>(this.api, {
      method: 'POST',
      path: '/auth/signup',
      body: { name, email, password },
      anonymous: true,
    });
    this.save(result);
    return result;
  }

  /** `POST /auth/logout` (204). O logout LOCAL é garantido mesmo se a rede falhar. */
  async logout(): Promise<void> {
    const refreshToken = this.stored?.refreshToken ?? null;
    this.clear();
    if (!refreshToken) {
      return;
    }
    try {
      await apiFetch<null>(this.api, {
        method: 'POST',
        path: '/auth/logout',
        body: { refreshToken },
        anonymous: true,
      });
    } catch {
      // O token já saiu do disco: no servidor, o refresh rotativo morre no próximo uso.
    }
  }

  /**
   * Renova o par. Single-flight + trava entre abas: se outra aba detém o lock,
   * esta espera o par novo no disco em vez de rotacionar o mesmo `rt_` (o que
   * faria o backend revogar a família inteira e deslogar as duas).
   */
  refresh(): Promise<string | null> {
    if (this.refreshing) {
      return this.refreshing;
    }
    const current = this.stored;
    if (!current) {
      return Promise.resolve(null);
    }
    const run = (async (): Promise<string | null> => {
      if (!this.acquireRefreshLock()) {
        return this.awaitRotatedToken(current);
      }
      try {
        const result = await apiFetch<AuthResult>(this.api, {
          method: 'POST',
          path: '/auth/refresh',
          body: { refreshToken: current.refreshToken },
          anonymous: true,
        });
        // O backend rotaciona: grava o par NOVO (HYBRID §8, saveSession a cada rotação).
        this.save({
          ...result,
          account: result.account ?? { id: current.accountId, name: '', email: '', createdAt: '' },
          user: result.user ?? { id: current.userId, email: '', name: '' },
        });
        return result.accessToken;
      } catch (error) {
        const code = (error as ApiError).code;
        this.log('refresh falhou', { code });
        if (code === 'NETWORK') {
          // Falha de rede NÃO invalida o refresh: a sessão em disco continua boa e
          // o próximo refresh tenta de novo. Só um 401 revoga mesmo.
          return null;
        }
        this.clear();
        this.onSessionLost?.();
        return null;
      } finally {
        this.releaseRefreshLock();
        this.refreshing = null;
      }
    })();
    this.refreshing = run;
    return run;
  }

  /**
   * Outra aba está rotacionando: espera o par novo no disco e relê.
   *
   * A comparação é pelo `refreshToken`, NÃO pelo `savedAt`: relógio em
   * milissegundos colide (login e rotação no mesmo ms são comuns), e uma colisão
   * faria esta aba desistir e rotacionar o mesmo `rt_` — exatamente o reuso que
   * revoga a família inteira.
   */
  private async awaitRotatedToken(previous: StoredSession): Promise<string | null> {
    const deadline = this.now().getTime() + LOCK_TTL_MS;
    while (this.now().getTime() < deadline) {
      await new Promise<void>(resolve => setTimeout(resolve, 150));
      const fresh = this.readStored();
      if (fresh && fresh.refreshToken !== previous.refreshToken) {
        this.stored = fresh;
        // O `BroadcastChannel` pode já ter adotado o access novo desta outra aba.
        // Sem ele (navegador antigo), `this.access` é null e a chamada que
        // esperou não autentica — mas NÃO pode rotacionar (reuso revoga a
        // família), então devolvemos null e a casca decide pelo próximo 401.
        return this.access;
      }
    }
    // A outra aba não concluiu: tenta o refresh agora (o lock já expirou).
    this.refreshing = null;
    return this.refresh();
  }

  /**
   * Call autenticada com refresh-no-401. Um `401 TOKEN_EXPIRED` renova e repete UMA
   * vez; qualquer outro `401` (revogado/reusado) limpa a sessão e propaga.
   */
  async call<T>(request: ApiRequest): Promise<T> {
    try {
      return await apiFetch<T>(this.api, request);
    } catch (error) {
      const code = (error as ApiError)?.code;
      if (code !== 'TOKEN_EXPIRED' && code !== 'UNAUTHENTICATED') {
        throw error;
      }
      const token = await this.refresh();
      if (!token) {
        throw error;
      }
      return apiFetch<T>(this.api, request);
    }
  }

  /**
   * Boot: garante access token em memória. Com refresh em disco e válido, o
   * gestor vai direto para `/escolher`; sem sessão, volta `false` para `/entrar`.
   *
   * O TIMEOUT é obrigatório: o `app.ts` mostra o esqueleto antes de pintar, e um
   * `fetch` pendurado em rede lenta deixaria a tela branca para sempre.
   */
  async ensureSession(): Promise<boolean> {
    if (this.access) {
      return true;
    }
    if (!this.stored) {
      return false;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<boolean>(resolve => {
      timer = setTimeout(() => {
        this.log('refresh de boot excedeu o tempo — seguindo sem sessão');
        resolve(false);
      }, this.bootTimeoutMs);
    });
    try {
      return await Promise.race([this.refresh().then(token => token !== null), timeout]);
    } finally {
      if (timer) {
        clearTimeout(timer);
      }
    }
  }

  async me(): Promise<{ account: Account; user: ManagerUser }> {
    return this.call<{ account: Account; user: ManagerUser }>({ method: 'GET', path: '/me' });
  }

  /** Encerra: solta canal e timers. A casca chama no `pagehide`. */
  dispose(): void {
    try {
      this.channel?.close();
    } catch {
      // Já fechado.
    }
    this.channel = null;
  }
}
